import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Star, ArrowRight, Globe, Flame, TreePine, Users, Gift } from 'lucide-react';
import { useLang } from '../../lib/i18n';
import { SEO, SCHEMA_RESTAURANT } from '../../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;
const ZENCHEF_URL_DEFAULT = "https://reserve.dish.co/346906";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&family=Manrope:wght@300;400;500;600;700&display=swap";

// Cache global de l'URL de réservation (lu depuis /api/site/settings)
let __RESERVATION_URL = ZENCHEF_URL_DEFAULT;
const useReservationUrl = () => {
  const [url, setUrl] = useState(__RESERVATION_URL);
  useEffect(() => {
    fetch(`${API}/api/site/settings`)
      .then(r => r.json())
      .then(s => {
        const v = s?.reservation?.zenchef_url;
        if (v && v.startsWith('http')) {
          __RESERVATION_URL = v;
          setUrl(v);
        }
      }).catch(() => {});
  }, []);
  return url;
};

const NAV_ITEMS = [
  { path: '/site/restaurant', label: 'Accueil' },
  { path: '/site/restaurant/menu', label: 'La Carte' },
  { path: '/site/restaurant/bons-cadeaux', label: 'Bons Cadeaux' },
  { path: '/site/restaurant/galerie', label: 'Galerie' },
  { path: '/site/restaurant/a-propos', label: 'A propos' },
  { path: '/site/restaurant/contact', label: 'Contact' },
];

const NAV_ITEMS_TRAITEUR = [
  { path: '/site/traiteur', label: 'Accueil' },
  { path: '/site/traiteur/prestations', label: 'Prestations' },
  { path: '/site/traiteur/plateaux', label: 'Plateaux' },
  { path: '/site/traiteur/galerie', label: 'Galerie' },
  { path: '/site/traiteur/contact', label: 'Contact' },
];

// ══════ SHARED HEADER ══════
export function SiteHeader({ section = 'restaurant' }) {
  const loc = useLocation();
  const nav = useNavigate();
  const isResto = section === 'restaurant';
  const [sections, setSections] = useState({});
  const [showLangMenu, setShowLangMenu] = useState(false);
  const { lang, setLang, t, FLAGS, LANGS } = useLang();
  const reservationUrl = useReservationUrl();

  useEffect(() => {
    fetch(`${API}/api/site/settings`).then(r => r.json()).then(s => setSections(s.sections || {})).catch(() => {});
  }, []);

  const NAV_ITEMS_I18N = [
    { path: '/site/restaurant', label: t('accueil') },
    { path: '/site/restaurant/menu', label: t('la_carte') },
    { path: '/site/restaurant/bons-cadeaux', label: t('bons_cadeaux') },
    { path: '/site/restaurant/galerie', label: t('galerie') },
    { path: '/site/restaurant/a-propos', label: t('a_propos') },
    { path: '/site/restaurant/contact', label: t('contact') },
  ];

  const NAV_ITEMS_TRAITEUR_I18N = [
    { path: '/site/traiteur', label: t('accueil') },
    { path: '/site/traiteur/prestations', label: t('prestations') },
    { path: '/site/traiteur/plateaux', label: t('plateaux') },
    { path: '/site/traiteur/galerie', label: t('galerie') },
    { path: '/site/traiteur/contact', label: t('contact') },
  ];

  const traiteurNav = NAV_ITEMS_TRAITEUR_I18N.filter(item => {
    if (item.path === '/site/traiteur/prestations' && sections.traiteur_prestations === false) return false;
    return true;
  });

  const items = isResto ? NAV_ITEMS_I18N : traiteurNav;
  const accentColor = isResto ? '#1a2744' : '#0d9488';
  const switchLabel = isResto ? t('traiteur') : t('restaurant');
  const switchPath = isResto ? '/site/traiteur' : '/site/restaurant';
  const ctaLabel = isResto ? t('reserver') : t('contact');
  const ctaPath = isResto ? reservationUrl : '/site/traiteur/contact';

  const currentFlag = FLAGS[lang];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" data-testid="site-header">
      <link href={FONT_LINK} rel="stylesheet" />
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to={isResto ? '/site/restaurant' : '/site/traiteur'} className="flex-shrink-0">
          {isResto ? (
            <img src="/images/logo-restaurant.jpg" alt="Le Mas" className="h-10" />
          ) : (
            <div className="h-10 w-10 rounded flex items-center justify-center" style={{ backgroundColor: accentColor }}>
              <img src="/images/logo-traiteur.png" alt="Traiteur du Mas" className="h-8" />
            </div>
          )}
        </Link>

        {/* Nav items */}
        <nav className="hidden md:flex items-center gap-6">
          {items.map(item => (
            <Link key={item.path} to={item.path}
              className={`text-sm font-['Manrope',sans-serif] transition-colors ${loc.pathname === item.path ? 'font-semibold border-b-2' : 'text-gray-600 hover:text-gray-900'}`}
              style={loc.pathname === item.path ? { color: accentColor, borderColor: accentColor } : {}}
            >{item.label}</Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Language selector */}
          <div className="relative">
            <button onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors"
              data-testid="lang-selector">
              <img src={currentFlag.flag} alt={lang.toUpperCase()} className="w-5 h-auto" />
              <span className="text-xs font-['Manrope',sans-serif] font-semibold text-gray-600">{lang.toUpperCase()}</span>
              <Globe size={13} className="text-gray-400" />
            </button>
            {showLangMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowLangMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[140px]" data-testid="lang-menu">
                  {LANGS.map(l => (
                    <button key={l} onClick={() => { setLang(l); setShowLangMenu(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${l === lang ? 'bg-gray-50' : ''}`}
                      data-testid={`lang-${l}`}>
                      <img src={FLAGS[l].flag} alt={l} className="w-5 h-auto" />
                      <span className="text-xs font-['Manrope',sans-serif] text-gray-700">{FLAGS[l].label}</span>
                      {l === lang && <span className="ml-auto text-[10px] text-gray-400">&#10003;</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button onClick={() => nav(switchPath)}
            className="hidden md:flex items-center gap-1 text-sm font-['Manrope',sans-serif] text-gray-600 hover:text-gray-900">
            {switchLabel} <ArrowRight size={14} />
          </button>
          <button onClick={() => isResto ? window.open(reservationUrl, '_blank') : nav(ctaPath)}
            className="text-xs font-bold font-['Manrope',sans-serif] tracking-[1.5px] uppercase text-white px-5 py-2.5"
            style={{ backgroundColor: accentColor }}
            data-testid="header-cta-btn"
          >{ctaLabel}</button>
        </div>
      </div>
    </header>
  );
}

// ══════ WHATSAPP BUTTON ══════
export function WhatsAppButton() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/site/whatsapp-config`)
      .then(r => r.json())
      .then(setConfig)
      .catch(() => setConfig({ enabled: true, number: '33468380698', message: 'Bonjour Le Mas !' }));
  }, []);

  if (!config || !config.enabled) return null;

  const url = `https://wa.me/${config.number}?text=${encodeURIComponent(config.message || '')}`;

  return (
    <a href={url}
      target="_blank" rel="noopener noreferrer"
      className="fixed bottom-24 right-6 z-40 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200"
      data-testid="whatsapp-btn"
      aria-label="Contacter via WhatsApp"
    >
      <svg viewBox="0 0 32 32" className="w-7 h-7 fill-white">
        <path d="M16.004 0h-.008C7.174 0 .002 7.174.002 16c0 3.5 1.128 6.744 3.046 9.378L1.06 31.502l6.318-1.958A15.907 15.907 0 0016.004 32C24.826 32 32 24.826 32 16S24.826 0 16.004 0zm9.302 22.602c-.39 1.1-1.932 2.014-3.168 2.282-.846.18-1.95.324-5.67-1.218-4.762-1.972-7.826-6.81-8.064-7.126-.23-.316-1.932-2.574-1.932-4.908s1.224-3.482 1.658-3.96c.434-.478.948-.598 1.264-.598.316 0 .632.002.908.016.292.014.682-.11 1.068.814.39.94 1.328 3.244 1.446 3.48.118.236.196.51.04.826-.158.316-.236.512-.472.788-.236.276-.496.616-.71.826-.236.236-.482.492-.208.964.276.472 1.224 2.02 2.63 3.272 1.808 1.608 3.332 2.106 3.804 2.342.472.236.748.196 1.024-.118.276-.316 1.184-1.382 1.5-1.86.316-.478.632-.394 1.068-.236.434.158 2.76 1.302 3.234 1.538.472.236.788.354.906.552.118.196.118 1.146-.274 2.248z"/>
      </svg>
    </a>
  );
}

// ══════ SHARED FOOTER ══════
export function SiteFooter({ section = 'restaurant' }) {
  const isResto = section === 'restaurant';
  const bgColor = isResto ? '#1a2744' : '#0d9488';
  const { t } = useLang();

  const items = isResto ? [
    { path: '/site/restaurant', label: t('accueil') },
    { path: '/site/restaurant/menu', label: t('la_carte') },
    { path: '/site/restaurant/bons-cadeaux', label: t('bons_cadeaux') },
    { path: '/site/restaurant/galerie', label: t('galerie') },
    { path: '/site/restaurant/a-propos', label: t('a_propos') },
    { path: '/site/restaurant/contact', label: t('contact') },
  ] : [
    { path: '/site/traiteur', label: t('accueil') },
    { path: '/site/traiteur/prestations', label: t('prestations') },
    { path: '/site/traiteur/plateaux', label: t('plateaux') },
    { path: '/site/traiteur/galerie', label: t('galerie') },
    { path: '/site/traiteur/contact', label: t('contact') },
  ];

  return (
    <footer style={{ backgroundColor: bgColor }} className="text-white py-16" data-testid="site-footer">
      <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Logo */}
        <div>
          {isResto ? (
            <img src="/logo-lemas.png" alt="Le Mas" className="h-20 mb-4 object-contain brightness-0 invert" />
          ) : (
            <img src="/images/logo-traiteur.png" alt="Traiteur du Mas" className="h-14 mb-4" />
          )}
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-[10px] font-bold font-['Manrope',sans-serif] tracking-[3px] uppercase mb-4 text-white/70">{t('contact')}</h4>
          <div className="space-y-2 text-sm font-['Manrope',sans-serif] text-white/60">
            <p className="flex items-start gap-2"><MapPin size={14} className="shrink-0 mt-0.5" /> 6 Av. Marechal Joffre<br/>66250 Saint-Laurent-de-la-Salanque</p>
            <a href="tel:0468380698" className="flex items-center gap-2 hover:text-white"><Phone size={14} /> 04 68 38 06 98</a>
          </div>
        </div>

        {/* Horaires */}
        <div>
          <h4 className="text-[10px] font-bold font-['Manrope',sans-serif] tracking-[3px] uppercase mb-4 text-white/70">{t('horaires')}</h4>
          <div className="space-y-1 text-sm font-['Manrope',sans-serif] text-white/60">
            <div className="flex justify-between"><span>{t('lundi')}</span><span>{t('ferme')}</span></div>
            <div className="flex justify-between"><span>{t('mardi')}</span><span>12h-14h / 18h30-22h</span></div>
            <div className="flex justify-between"><span>{t('mercredi')}</span><span>12h-14h</span></div>
            <div className="flex justify-between"><span>{t('jeudi')}</span><span>12h-14h / 18h30-22h</span></div>
            <div className="flex justify-between"><span>{t('vendredi')}</span><span>12h-14h / 18h30-22h</span></div>
            <div className="flex justify-between"><span>{t('samedi')}</span><span>12h-14h / 18h30-22h</span></div>
            <div className="flex justify-between"><span>{t('dimanche')}</span><span>12h-14h</span></div>
          </div>
        </div>

        {/* Navigation */}
        <div>
          <h4 className="text-[10px] font-bold font-['Manrope',sans-serif] tracking-[3px] uppercase mb-4 text-white/70">{t('navigation')}</h4>
          <div className="space-y-2">
            {items.map(item => (
              <Link key={item.path} to={item.path} className="block text-sm font-['Manrope',sans-serif] text-white/60 hover:text-white">{item.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ══════ RESTAURANT HOME ══════
export function RestaurantHome() {
  const nav = useNavigate();
  const [actualites, setActualites] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [bandeau, setBandeau] = useState(null);
  const { t } = useLang();
  const reservationUrl = useReservationUrl();

  useEffect(() => {
    fetch(`${API}/api/site/actualites`).then(r => r.json()).then(setActualites).catch(() => {});
    fetch(`${API}/api/site/evenements`).then(r => r.json()).then(setEvenements).catch(() => {});
    fetch(`${API}/api/site/display-settings`).then(r => r.json()).then(d => {
      if (d.bandeau_bons_cadeaux) setBandeau(d);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',Georgia,serif]" data-testid="restaurant-home">
      <SEO
        title="Restaurant Mediterraneen"
        description="Le Mas, restaurant et traiteur a Saint-Laurent-de-la-Salanque (66250). Cuisine mediterraneenne, catalane et francaise. Terrasse, salle privee, menu du jour. Reservez au 04 68 38 06 98."
        path="/site/restaurant"
        schema={SCHEMA_RESTAURANT}
      />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      {/* Hero */}
      <div className="relative min-h-screen flex items-center justify-center" data-testid="restaurant-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/restaurant/hero.jpg)' }} />
        <div className="absolute inset-0 bg-[#1a2744]/55" />
        <div className="relative z-10 text-center px-6 max-w-3xl">
          <p className="text-white/60 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-5">Saint-Laurent-de-la-Salanque</p>
          <h1 className="text-4xl md:text-6xl font-light text-white leading-tight mb-2">{t('hero_line1')}</h1>
          <h1 className="text-4xl md:text-6xl italic text-white/90 leading-tight mb-6">{t('hero_line2')}</h1>
          <p className="text-white/60 text-base font-['Manrope',sans-serif] leading-relaxed mb-8 max-w-xl mx-auto">
            {t('hero_desc')}
          </p>

          {/* Stars */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1,2,3,4].map(i => <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />)}
            <Star size={18} className="text-yellow-400 fill-yellow-400/50" />
            <span className="text-white font-bold text-base font-['Manrope',sans-serif] ml-2">4.41</span>
            <span className="text-white/50 text-sm font-['Manrope',sans-serif]">• 423 {t('avis_google')}</span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => window.open(reservationUrl, '_blank')}
              className="border-2 border-white text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors"
              data-testid="hero-reserve-btn">
              {t('reserver_table')}
            </button>
            <button onClick={() => nav('/site/restaurant/menu')}
              className="border-2 border-white/50 text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors"
              data-testid="hero-carte-btn">
              {t('voir_carte')}
            </button>
          </div>
        </div>
      </div>

      {/* Bandeau Bons Cadeaux */}
      {bandeau && (
        <div className="bg-[#1a2744] py-6" data-testid="bandeau-bons-cadeaux">
          <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Gift size={28} className="text-[#0d9488] shrink-0" />
              <div>
                <p className="text-white text-xl font-light">{bandeau.bandeau_texte || "Offrez un moment d'exception"}</p>
                <p className="text-white/50 text-sm font-['Manrope',sans-serif]">{bandeau.bandeau_sous_texte || "Decouvrez nos bons cadeaux a offrir en ligne"}</p>
              </div>
            </div>
            <button onClick={() => nav('/site/restaurant/bons-cadeaux')}
              className="border-2 border-[#0d9488] text-[#0d9488] px-8 py-3 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#0d9488] hover:text-white transition-all whitespace-nowrap"
              data-testid="bandeau-cta-btn">
              Offrir un bon cadeau
            </button>
          </div>
        </div>
      )}

      {/* Services */}
      <div className="py-24 bg-gray-50/50">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-center text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">Nos Services</p>
          <h2 className="text-center text-4xl font-light mb-16">Un cadre d'exception</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Flame, title: 'Cheminee', desc: 'Ambiance chaleureuse' },
              { icon: TreePine, title: 'Terrasse', desc: 'Espace exterieur' },
              { icon: Users, title: 'Salle privee', desc: 'Pour vos evenements' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-100 p-10 text-center hover:shadow-md transition-shadow" data-testid={`service-${i}`}>
                <s.icon size={36} className="mx-auto mb-5 text-[#1a2744]" strokeWidth={1.2} />
                <h3 className="text-xl mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm font-['Manrope',sans-serif]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Actualites & Evenements */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-center text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">A ne pas manquer</p>
          <h2 className="text-center text-4xl font-light mb-16">Actualites & Evenements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Evenements */}
            <div>
              <h3 className="text-lg font-['Manrope',sans-serif] font-semibold text-gray-700 mb-4">Evenements a venir</h3>
              {evenements.length > 0 ? evenements.slice(0, 3).map((evt, i) => {
                const d = evt.event_date ? new Date(evt.event_date) : null;
                return (
                  <div key={i} className="bg-white rounded-lg border border-gray-100 p-5 mb-3 flex gap-4" data-testid={`event-${i}`}>
                    {d && (
                      <div className="bg-[#1a2744] text-white rounded-lg px-3 py-2 text-center min-w-[56px] flex-shrink-0">
                        <span className="text-2xl font-bold block leading-none">{d.getDate()}</span>
                        <span className="text-[10px] uppercase">{d.toLocaleDateString('fr-FR', { month: 'short' })}</span>
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-base mb-1">{evt.title}</h4>
                      <p className="text-gray-400 text-xs font-['Manrope',sans-serif]">{evt.description}</p>
                    </div>
                  </div>
                );
              }) : (
                <div className="bg-white rounded-lg border border-gray-100 p-5 text-center">
                  <p className="text-gray-400 text-sm font-['Manrope',sans-serif]">Aucun evenement a venir pour le moment</p>
                </div>
              )}
            </div>
            {/* Actualites */}
            <div>
              <h3 className="text-lg font-['Manrope',sans-serif] font-semibold text-gray-700 mb-4">Actualites</h3>
              {actualites.length > 0 ? actualites.slice(0, 3).map((a, i) => (
                <div key={i} className="bg-white rounded-lg border border-gray-100 p-5 mb-3" data-testid={`actu-${i}`}>
                  <h4 className="font-semibold text-base mb-1">{a.title}</h4>
                  <p className="text-gray-500 text-sm font-['Manrope',sans-serif] mb-2">{a.content}</p>
                  <p className="text-gray-300 text-xs font-['Manrope',sans-serif]">Publie le {new Date(a.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              )) : (
                <div className="bg-white rounded-lg border border-gray-100 p-5 text-center">
                  <p className="text-gray-400 text-sm font-['Manrope',sans-serif]">Aucune actualite pour le moment</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notre Histoire */}
      <div className="py-24">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src="/images/restaurant/team.jpg" alt="L'equipe du Mas" className="w-full rounded-lg shadow-lg" />
            <div className="absolute bottom-0 right-0 bg-[#1a2744] text-white px-6 py-4 rounded-tl-lg">
              <span className="text-3xl font-light italic block leading-none">15</span>
              <span className="text-[10px] tracking-[2px] uppercase font-['Manrope',sans-serif]">{t('ans_experience')}</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">{t('histoire_sub')}</p>
            <h2 className="text-4xl font-light mb-1">{t('passion1')}</h2>
            <h2 className="text-4xl italic mb-6">{t('passion2')}</h2>
            <p className="text-gray-500 text-base font-['Manrope',sans-serif] leading-relaxed mb-4">
              {t('histoire_p1')}
            </p>
            <p className="text-gray-400 text-sm font-['Manrope',sans-serif] leading-relaxed mb-6">
              {t('histoire_p2')}
            </p>
            <button onClick={() => nav('/site/restaurant/a-propos')}
              className="text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase text-[#1a2744] flex items-center gap-2 hover:gap-3 transition-all"
              data-testid="histoire-link">
              {t('en_savoir_plus')} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Galerie Preview */}
      <div className="py-24 bg-gray-50/50">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-center text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">{t('galerie_title')}</p>
          <h2 className="text-center text-4xl font-light mb-12">{t('art_table')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[3, 4, 6, 8].map(n => (
              <img key={n} src={`/images/restaurant/gallery/gallery${n}.jpg`} alt={`Galerie ${n}`}
                className="w-full aspect-[4/5] object-cover rounded-lg hover:opacity-90 transition-opacity cursor-pointer"
                onClick={() => nav('/site/restaurant/galerie')}
              />
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => nav('/site/restaurant/galerie')}
              className="bg-[#1a2744] text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#243558] transition-colors"
              data-testid="galerie-btn">
              {t('voir_galerie')}
            </button>
          </div>
        </div>
      </div>

      {/* Reservation CTA */}
      <div className="py-20 bg-[#1a2744] text-white text-center">
        <h2 className="text-4xl font-light mb-4">{t('reservez_titre')}</h2>
        <p className="text-white/50 text-base font-['Manrope',sans-serif] mb-8 max-w-lg mx-auto">
          {t('reservez_desc')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={() => window.open(reservationUrl, '_blank')}
            className="border-2 border-white text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors">
            {t('reserver_en_ligne')}
          </button>
          <a href="tel:0468380698"
            className="border-2 border-white/30 text-white/70 px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
            <Phone size={14} /> 04 68 38 06 98
          </a>
        </div>
      </div>

      {/* Nous Trouver */}
      <div className="py-24">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">{t('contact')}</p>
            <h2 className="text-4xl font-light mb-10">{t('venez')}</h2>
            <div className="space-y-6">
              <div>
                <p className="flex items-center gap-2 text-sm font-['Manrope',sans-serif] font-semibold text-gray-700 mb-1"><MapPin size={16} className="text-[#1a2744]" /> {t('adresse')}</p>
                <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">6 Av. Marechal Joffre</p>
                <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">66250 Saint-Laurent-de-la-Salanque</p>
              </div>
              <div>
                <p className="flex items-center gap-2 text-sm font-['Manrope',sans-serif] font-semibold text-gray-700 mb-1"><Clock size={16} className="text-[#1a2744]" /> {t('horaires')}</p>
                <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">{t('mardi')}, {t('jeudi')}-{t('samedi')} : 12h-14h / 18h30-22h</p>
                <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">{t('mercredi')}, {t('dimanche')} : 12h-14h</p>
                <p className="text-gray-400 text-sm font-['Manrope',sans-serif] ml-6 font-semibold">{t('lundi')} : {t('ferme')}</p>
              </div>
            </div>
          </div>
          <div>
            <iframe
              title="Le Mas Restaurant"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2947.5!2d2.994!3d42.772!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b06f0c3d4eaaab%3A0xabcdef!2s6+Av.+Mar%C3%A9chal+Joffre%2C+66250+Saint-Laurent-de-la-Salanque!5e0!3m2!1sfr!2sfr"
              width="100%" height="350"
              style={{ border: 0, borderRadius: '12px' }}
              loading="lazy"
              allowFullScreen
            />
          </div>
        </div>
      </div>

      <WhatsAppButton />
      <SiteFooter section="restaurant" />
    </div>
  );
}

// ══════ RESTAURANT MENU ══════
export function RestaurantMenu() {
  const [carte, setCarte] = useState(null);
  const [menuDuJour, setMenuDuJour] = useState(null);
  const [showPrix, setShowPrix] = useState(true);
  const { lang, t } = useLang();
  const [translatedCarte, setTranslatedCarte] = useState(null);
  const [translatedMenu, setTranslatedMenu] = useState(null);
  const [translating, setTranslating] = useState(false);
  const reservationUrl = useReservationUrl();

  useEffect(() => {
    const ts = Date.now();
    fetch(`${API}/api/site/carte?_t=${ts}`, { cache: 'no-store' }).then(r => r.json()).then(d => {
      setCarte(d);
      if (d.afficher_prix === false) setShowPrix(false);
    }).catch(() => {});
    fetch(`${API}/api/menu-du-jour/public?_t=${ts}`, { cache: 'no-store' }).then(r => r.json()).then(setMenuDuJour).catch(() => {});
  }, []);

  // Translate carte items when language changes
  useEffect(() => {
    if (lang === 'fr' || !carte?.categories) {
      setTranslatedCarte(null);
      return;
    }
    // Only translate food categories (skip wines, beers, drinks - names stay same)
    const FOOD_CATS = ['tapas', 'entree', 'plat_viande', 'plat_poisson', 'garniture', 'dessert', 'Tapas', 'Entrees', 'Viandes', 'Poissons', 'Garnitures', 'Desserts'];
    const foodItems = Object.entries(carte.categories)
      .filter(([cat]) => FOOD_CATS.includes(cat))
      .flatMap(([cat, items]) => items.map(item => ({ ...item, _cat: cat })));
    
    if (foodItems.length === 0) { setTranslatedCarte(null); return; }
    setTranslating(true);
    fetch(`${API}/api/translate/menu`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: foodItems, lang })
    }).then(r => r.json()).then(data => {
      if (data.items) {
        // Build translated categories from response
        const translatedCats = {};
        data.items.forEach(item => {
          const cat = item._cat;
          if (!translatedCats[cat]) translatedCats[cat] = [];
          translatedCats[cat].push(item);
        });
        // Merge: use translated for food, keep original for drinks
        const merged = {};
        for (const [cat, items] of Object.entries(carte.categories)) {
          merged[cat] = translatedCats[cat] || items;
        }
        setTranslatedCarte(merged);
      }
    }).catch(e => console.error('Translate carte error:', e)).finally(() => setTranslating(false));
  }, [lang, carte]);

  // Translate menu du jour items when language changes
  useEffect(() => {
    if (lang === 'fr' || !menuDuJour) {
      setTranslatedMenu(null);
      return;
    }
    const allItems = [
      ...(menuDuJour.entrees || []).map(e => ({ ...e, _type: 'entree' })),
      ...(menuDuJour.plats || []).map(p => ({ ...p, _type: 'plat' })),
      ...(menuDuJour.desserts || []).map(d => ({ ...d, _type: 'dessert' })),
    ];
    if (allItems.length === 0) return;
    fetch(`${API}/api/translate/menu`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: allItems, lang })
    }).then(r => r.json()).then(data => {
      if (data.items) {
        const result = { entrees: [], plats: [], desserts: [] };
        data.items.forEach(item => {
          if (item._type === 'entree') result.entrees.push(item);
          else if (item._type === 'plat') result.plats.push(item);
          else if (item._type === 'dessert') result.desserts.push(item);
        });
        setTranslatedMenu(result);
      }
    }).catch(() => {});
  }, [lang, menuDuJour]);

  // Use translated or original data
  const displayMenu = translatedMenu || menuDuJour;
  const displayCategories = translatedCarte || carte?.categories;

  const CATEGORY_ORDER = ['Tapas', 'tapas', 'Entrees', 'entree', 'Viandes', 'plat_viande', 'Poissons', 'plat_poisson', 'Garnitures', 'garniture', 'Desserts', 'dessert', 'Aperitifs', 'aperitif', 'Softs', 'soft', 'Cafes', 'cafe', 'Bieres', 'biere', 'Champagne', 'champagne', 'Spiritueux', 'spiritueux', 'Vins Blancs', 'vin_blanc', 'Vins Roses', 'vin_rose', 'Vins Rouges', 'vin_rouge'];
  const LABEL_MAP = { tapas: 'Tapas', entree: 'Entrees', plat_viande: 'Plats Viande', plat_poisson: 'Plats Poisson', garniture: 'Garnitures', dessert: 'Desserts', aperitif: 'Aperitifs', soft: 'Softs', cafe: 'Cafes', biere: 'Bieres', champagne: 'Champagne', spiritueux: 'Spiritueux', vin_blanc: 'Vins Blancs', vin_rose: 'Vins Roses', vin_rouge: 'Vins Rouges' };
  const formatCat = (cat) => LABEL_MAP[cat] || t(cat) || cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const sortedCategories = displayCategories
    ? Object.entries(displayCategories).sort(([a], [b]) => {
        const ia = CATEGORY_ORDER.indexOf(a);
        const ib = CATEGORY_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
    : [];

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="restaurant-menu">
      <SEO
        title="La Carte & Menu du Jour"
        description="Decouvrez la carte du restaurant Le Mas a Saint-Laurent-de-la-Salanque. Menu du jour, entrees, plats, desserts, vins. Cuisine catalane et mediterraneenne."
        path="/site/restaurant/menu"
        schema={{
          "@context": "https://schema.org",
          "@type": "Menu",
          "name": "La Carte du Mas",
          "description": "Carte et menu du jour du restaurant Le Mas",
          "hasMenuSection": [
            { "@type": "MenuSection", "name": "Entrees" },
            { "@type": "MenuSection", "name": "Plats" },
            { "@type": "MenuSection", "name": "Desserts" },
            { "@type": "MenuSection", "name": "Vins" }
          ]
        }}
      />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      <div className="relative h-[50vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/restaurant/carte.jpg)' }} />
        <div className="absolute inset-0 bg-[#1a2744]/70" />
        <div className="relative z-10 text-center px-6">
          <p className="text-white/50 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-3">{t('menu_sub')}</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">{t('menu_title')}</h1>
          {translating && (
            <p className="text-white/40 text-[10px] tracking-[3px] uppercase font-['Manrope',sans-serif] mt-3 animate-pulse" data-testid="translating-indicator">
              {lang === 'en' ? 'Translating...' : lang === 'es' ? 'Traduciendo...' : lang === 'ca' ? 'Traduint...' : 'Traduction...'}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-20">
        {/* Menu du Jour dynamique */}
        {menuDuJour && menuDuJour.mode === 'menu_du_jour' && (
          <div className="border border-gray-200 rounded-lg p-8 mb-12" data-testid="menu-du-jour-public">
            <p className="text-center text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-2">{t('suggestion_du')} {menuDuJour.jour_label || ''}</p>
            <h2 className="text-center text-3xl font-light mb-8">{menuDuJour.titre || t('menu_du_jour')}</h2>
            {(displayMenu?.entrees || menuDuJour.entrees)?.length > 0 && (
              <div className="mb-6">
                <p className="text-center text-[#1a2744] text-xs font-['Manrope',sans-serif] font-bold tracking-[2px] uppercase mb-3">{t('entrees')}</p>
                {(displayMenu?.entrees || menuDuJour.entrees).map((e, i) => (
                  <div key={i} className="text-center mb-2">
                    <p className="text-base">{e.nom || e}</p>
                    {e.description && <p className="text-gray-400 text-sm italic">{e.description}</p>}
                  </div>
                ))}
              </div>
            )}
            {(displayMenu?.plats || menuDuJour.plats)?.length > 0 && (
              <div className="mb-6">
                <div className="w-16 h-px bg-gray-200 mx-auto mb-4" />
                <p className="text-center text-[#1a2744] text-xs font-['Manrope',sans-serif] font-bold tracking-[2px] uppercase mb-3">{t('plats')}</p>
                {(displayMenu?.plats || menuDuJour.plats).map((p, i) => (
                  <div key={i} className="text-center mb-2">
                    <p className="text-base">{p.nom || p}</p>
                    {p.accompagnement && <p className="text-gray-400 text-sm italic">{p.accompagnement}</p>}
                  </div>
                ))}
              </div>
            )}
            {(displayMenu?.desserts || menuDuJour.desserts)?.length > 0 && (
              <div className="mb-6">
                <div className="w-16 h-px bg-gray-200 mx-auto mb-4" />
                <p className="text-center text-[#1a2744] text-xs font-['Manrope',sans-serif] font-bold tracking-[2px] uppercase mb-3">{t('desserts')}</p>
                {(displayMenu?.desserts || menuDuJour.desserts).map((d, i) => (
                  <div key={i} className="text-center mb-2">
                    <p className="text-base">{d.nom || d}</p>
                    {d.description && <p className="text-gray-400 text-sm italic">{d.description}</p>}
                  </div>
                ))}
              </div>
            )}
            {menuDuJour.formules?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-center text-[10px] tracking-[3px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-4">{t('formules')}</p>
                <div className="flex flex-wrap justify-center gap-6">
                  {menuDuJour.formules.map((f, i) => (
                    <div key={i} className="text-center">
                      <p className="text-2xl font-light text-[#1a2744]">{f.prix}€</p>
                      <p className="text-gray-400 text-xs font-['Manrope',sans-serif] mt-1">{f.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Formules seules (weekend / pas de menu du jour) */}
        {menuDuJour && menuDuJour.mode === 'formules_only' && menuDuJour.formules?.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-8 mb-12" data-testid="formules-only-public">
            <p className="text-center text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-2">{t('midi')}</p>
            <h2 className="text-center text-3xl font-light mb-8">{menuDuJour.titre || t('nos_formules')}</h2>
            <div className="flex flex-wrap justify-center gap-6">
              {menuDuJour.formules.map((f, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl font-light text-[#1a2744]">{f.prix}€</p>
                  <p className="text-gray-400 text-xs font-['Manrope',sans-serif] mt-1">{f.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Carte dynamique depuis l'admin */}
        {sortedCategories.length > 0 ? (
          <div className="space-y-10">
            <div className="text-center mb-8">
              <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-2">{t('notre')}</p>
              <h2 className="text-3xl font-light">{t('carte')}</h2>
            </div>
            {sortedCategories.map(([cat, items]) => (
              <div key={cat}>
                <h3 className="text-xl text-[#1a2744] mb-4 pb-2 border-b border-gray-100">{formatCat(cat)}</h3>
                <div className="space-y-3">
                  {items.map((item, i) => (
                    <div key={i} className="flex justify-between items-baseline gap-4" data-testid={`carte-item-${i}`}>
                      <div className="flex-1">
                        <p className="text-base">{item.nom}</p>
                        {item.description && <p className="text-gray-400 text-xs font-['Manrope',sans-serif]">{item.description}</p>}
                      </div>
                      {showPrix && item.prix > 0 && (
                        <p className="text-[#1a2744] text-base font-['Manrope',sans-serif] font-semibold whitespace-nowrap">{item.prix.toFixed(2)}€</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400 text-sm font-['Manrope',sans-serif]">{t('carte_bientot')}</p>
          </div>
        )}

        <div className="text-center mt-12">
          <button onClick={() => window.open(reservationUrl, '_blank')}
            className="bg-[#1a2744] text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#243558] transition-colors">
            {t('reserver_table')}
          </button>
        </div>
      </div>

      <WhatsAppButton />
      <SiteFooter section="restaurant" />
    </div>
  );
}

// ══════ RESTAURANT GALERIE ══════
export function RestaurantGalerie() {
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/site/gallery?section=restaurant`).then(r => r.json()).then(setPhotos).catch(() => {});
  }, []);

  const defaultPhotos = Array.from({ length: 35 }, (_, i) => ({ id: `r-gal-${i+1}`, url: `/images/restaurant/gallery/gallery${i+1}.jpg` }));
  const items = photos.length > 0 ? photos : defaultPhotos;

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="restaurant-galerie">
      <SEO title="Galerie Photos" description="Decouvrez Le Mas en images. Terrasse, salle, plats, evenements. Restaurant mediterraneen a Saint-Laurent-de-la-Salanque." path="/site/restaurant/galerie" />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      <div className="pt-24 pb-16">
        <div className="text-center py-12">
          <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">Restaurant Le Mas</p>
          <h1 className="text-4xl md:text-5xl font-light">Galerie Photos</h1>
        </div>
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((p, i) => (
              <div key={p.id || i}
                className="aspect-square bg-cover bg-center rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundImage: `url(${p.url})` }}
                onClick={() => setLightbox(p.url)}
                data-testid={`restaurant-photo-${i}`}
              />
            ))}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Photo" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        </div>
      )}

      <WhatsAppButton />
      <SiteFooter section="restaurant" />
    </div>
  );
}

// ══════ RESTAURANT ABOUT ══════
export function RestaurantAbout() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="restaurant-about">
      <SEO title="A Propos" description="Le Mas, restaurant familial a Saint-Laurent-de-la-Salanque depuis des annees. Cuisine catalane, produits locaux, terrasse ombragee. Decouvrez notre histoire." path="/site/restaurant/a-propos" />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      <div className="relative h-[50vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/restaurant/about.jpg)' }} />
        <div className="absolute inset-0 bg-[#1a2744]/70" />
        <div className="relative z-10 text-center px-6">
          <p className="text-white/50 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-3">Notre histoire</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">A propos du Mas</h1>
        </div>
      </div>

      {/* Quote */}
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h2 className="text-3xl font-light leading-relaxed mb-6">
          C'est une histoire de famille et de terroir. L'histoire d'un coup de coeur pour une batisse noble et chaleureuse, enracinee au sein du pays catalan.
        </h2>
        <p className="text-gray-400 text-sm font-['Manrope',sans-serif] leading-relaxed max-w-xl mx-auto">
          Retrouvez une ambiance conviviale au sein de ce village uni et colore. Il fait bon y vivre et y venir pour boire un apero et pour y manger des mets locaux et savoureux.
        </p>
      </div>

      {/* Laurent + History */}
      <div className="bg-gray-50 py-24">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src="/images/restaurant/team.jpg" alt="Laurent et son pere" className="w-full rounded-lg shadow-lg" />
            <div className="absolute bottom-0 right-0 bg-[#1a2744] text-white px-6 py-4 rounded-tl-lg">
              <span className="text-3xl font-light italic block leading-none">15</span>
              <span className="text-[10px] tracking-[2px] uppercase font-['Manrope',sans-serif]">ans d'experience</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[4px] uppercase font-['Manrope',sans-serif] text-gray-400 mb-3">Le fondateur</p>
            <h2 className="text-3xl font-light mb-6">Laurent San Martin</h2>
            <p className="text-gray-500 text-base font-['Manrope',sans-serif] leading-relaxed mb-4">
              Un retour aux sources pour Laurent Sanmartin, sur les terres de son enfance, Saint-Laurent-de-la-Salanque.
            </p>
            <p className="text-gray-400 text-sm font-['Manrope',sans-serif] leading-relaxed mb-4">
              En 2009, il tombe amoureux d'un vieux mas, ayant abrite une cave a vins et un atelier de menuiserie. Avec l'aide de son pere, il decide de restaurer ce lieu charge d'histoire pour en faire un restaurant authentique.
            </p>
            <p className="text-gray-400 text-sm font-['Manrope',sans-serif] leading-relaxed">
              Ouvert a l'annee, midi et soir, nous travaillons avec les producteurs de Saint-Laurent et ceux des Pyrenees orientales. Venez passer un bon moment en notre compagnie.
            </p>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="max-w-5xl mx-auto px-4 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            { title: 'Engage par conviction', desc: 'Le Mas renforce sa position de marque repere en elargissant son expertise et sa philosophie. Avec Le Mas, vous avez la garantie de ne jamais vous tromper.', img: '/images/restaurant/about1.jpg' },
            { title: 'Un lieu de detente', desc: 'Vous etes a la recherche d\'un lieu authentique ? Le Mas vous propose une terrasse ombragee, decoree avec beaucoup de subtilite.', img: '/images/restaurant/about2.jpg' },
          ].map((v, i) => (
            <div key={i} className="rounded-lg overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="h-52 bg-cover bg-center" style={{ backgroundImage: `url(${v.img})` }} />
              <div className="p-6">
                <h3 className="text-xl mb-3">{v.title}</h3>
                <p className="text-gray-400 text-sm font-['Manrope',sans-serif] leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rating */}
      <div className="text-center pb-24">
        <div className="flex items-center justify-center gap-2">
          {[1,2,3,4].map(i => <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />)}
          <Star size={18} className="text-yellow-400 fill-yellow-400/50" />
          <span className="text-2xl font-light text-gray-800 ml-2">4.41</span>
          <span className="text-gray-400 text-sm font-['Manrope',sans-serif]">/ 423 avis Google</span>
        </div>
      </div>

      <WhatsAppButton />
      <SiteFooter section="restaurant" />
    </div>
  );
}

// ══════ RESTAURANT CONTACT ══════
export function RestaurantContact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API}/api/site/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, section: 'restaurant' })
      });
      setSent(true);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="restaurant-contact">
      <SEO title="Contact & Reservation" description="Contactez Le Mas au 04 68 38 06 98. 6 Avenue Marechal Joffre, Saint-Laurent-de-la-Salanque 66250. Reservez en ligne ou par telephone." path="/site/restaurant/contact" />
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      <div className="relative h-[40vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/restaurant/contact.jpg)' }} />
        <div className="absolute inset-0 bg-[#1a2744]/70" />
        <div className="relative z-10 text-center px-6">
          <p className="text-white/50 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-3">Restaurant Le Mas</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">Contact</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-20 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div>
            <p className="flex items-center gap-2 text-sm font-['Manrope',sans-serif] font-semibold text-gray-700 mb-1"><MapPin size={16} className="text-[#1a2744]" /> Adresse</p>
            <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">6 Av. Marechal Joffre</p>
            <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">66250 Saint-Laurent-de-la-Salanque</p>
          </div>
          <div>
            <p className="flex items-center gap-2 text-sm font-['Manrope',sans-serif] font-semibold text-gray-700 mb-1"><Phone size={16} className="text-[#1a2744]" /> Telephone</p>
            <a href="tel:0468380698" className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6 hover:text-[#1a2744]">04 68 38 06 98</a>
          </div>
          <div>
            <p className="flex items-center gap-2 text-sm font-['Manrope',sans-serif] font-semibold text-gray-700 mb-1"><Clock size={16} className="text-[#1a2744]" /> Horaires</p>
            <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">Mardi, Jeudi-Samedi : 12h-14h / 18h30-22h</p>
            <p className="text-gray-500 text-sm font-['Manrope',sans-serif] ml-6">Mercredi, Dimanche : 12h-14h</p>
            <p className="text-gray-400 text-sm font-['Manrope',sans-serif] ml-6 font-semibold">Lundi : Ferme</p>
          </div>
          <div className="pt-2">
            <iframe title="Le Mas" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2947.5!2d2.994!3d42.772!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b06f0c3d4eaaab%3A0xabcdef!2s6+Av.+Mar%C3%A9chal+Joffre%2C+66250+Saint-Laurent-de-la-Salanque!5e0!3m2!1sfr!2sfr" width="100%" height="220" style={{ border: 0, borderRadius: '12px' }} loading="lazy" />
          </div>
        </div>
        <div>
          {sent ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <h3 className="text-3xl font-light mb-3 text-[#1a2744]">Merci !</h3>
              <p className="text-gray-500 text-sm font-['Manrope',sans-serif]">Nous vous repondrons dans les plus brefs delais.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="restaurant-contact-form">
              <input type="text" placeholder="Nom" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20" data-testid="resto-contact-name" />
              <input type="email" placeholder="Email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20" data-testid="resto-contact-email" />
              <input type="tel" placeholder="Telephone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20" data-testid="resto-contact-phone" />
              <textarea placeholder="Votre message" required rows={5} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Manrope',sans-serif] placeholder:text-gray-300 focus:border-[#1a2744] focus:outline-none focus:ring-1 focus:ring-[#1a2744]/20 resize-none" data-testid="resto-contact-message" />
              <button type="submit" className="w-full bg-[#1a2744] text-white py-3.5 rounded-lg text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#243558] transition-colors" data-testid="resto-contact-submit">
                Envoyer
              </button>
            </form>
          )}
        </div>
      </div>
      <WhatsAppButton />
      <SiteFooter section="restaurant" />
    </div>
  );
}

// ══════ RESTAURANT GROUPE ══════
export function RestaurantGroupe() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Cormorant_Garamond',serif]" data-testid="restaurant-groupe">
      <link href={FONT_LINK} rel="stylesheet" />
      <SiteHeader section="restaurant" />

      <div className="relative h-[50vh] flex items-center justify-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/restaurant/groupe.jpg)' }} />
        <div className="absolute inset-0 bg-[#1a2744]/70" />
        <div className="relative z-10 text-center px-6">
          <p className="text-white/50 text-[10px] tracking-[5px] uppercase font-['Manrope',sans-serif] mb-3">Jusqu'a 150 personnes</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">Accueil des groupes</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-light mb-6">En groupe professionnel, entre amis ou en famille</h2>
          <p className="text-gray-500 text-base font-['Manrope',sans-serif] leading-relaxed max-w-2xl mx-auto">
            Notre equipe mettra tout en oeuvre pour satisfaire le moindre de vos desirs. Nous vous proposerons des menus adaptes a vos attentes et selon vos envies.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
          {['Reunion', 'Seminaire', 'Networking', 'Fete', 'Bapteme', 'Mariage'].map((e, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-md transition-shadow">
              <p className="text-gray-600 text-sm font-['Manrope',sans-serif] font-medium">{e}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button onClick={() => nav('/site/restaurant/contact')}
            className="bg-[#1a2744] text-white px-10 py-4 text-[11px] font-bold font-['Manrope',sans-serif] tracking-[2px] uppercase hover:bg-[#243558] transition-colors">
            Contactez-nous
          </button>
        </div>
      </div>
      <WhatsAppButton />
      <SiteFooter section="restaurant" />
    </div>
  );
}

export { NAV_ITEMS_TRAITEUR };
